import { get as dig, uniqBy } from 'lodash';
import * as immutable from 'object-path-immutable';

export const graphQLIdDecoder = (nodeId?: string) => {
  const s = typeof window.atob === 'function' ? window.atob(nodeId) : '';
  const re = /gid:\/\/company-cam\/(.*)\/(.*)/;
  const matches = s.match(re);

  if (Array.isArray(matches) && matches.length === 3) {
    return {
      type: matches[1],
      databaseId: matches[2],
    };
  } else {
    return { type: '', databaseId: 0 };
  }
};

export const graphQLDatabaseIdIntDecoder = (nodeId?: string) => {
  const decodedId = graphQLIdDecoder(nodeId);
  return decodedId && typeof decodedId.databaseId === 'string'
    ? parseInt(decodedId.databaseId, 10)
    : undefined;
};

export const graphQLIdGenerator = (type: string, databaseId: string | number) =>
  window.btoa(`gid://company-cam/${type}/${databaseId}`);

export const graphQLIdFromPhotoDBID = (dbId: string | number) =>
  graphQLIdGenerator('Image', dbId);
export const graphQLIdFromVideoDBID = (dbId: string | number) =>
  graphQLIdGenerator('Video', dbId);
export const graphQLIdFromProjectDBID = (dbId: string | number) =>
  graphQLIdGenerator('Location', dbId);

type PageInfoType = {
  hasNextPage: boolean;
  endCursor: string | null;
};
const DEFAULT_PAGEINFO: PageInfoType = { hasNextPage: false, endCursor: null };

export const edgesWithoutNodeIds = (
  edges: any,
  ids: string | Array<string>,
) => {
  const nodeIds = Array.isArray(ids) ? ids : [ids];

  const idMap: Record<string, any> = {};
  nodeIds.forEach((id) => {
    idMap[id] = true;
  });

  return edges.filter((edge: any) => !idMap.hasOwnProperty(edge.node.id));
};

export const pageInfoFromData = (
  data: any,
  path: string,
  fallback = DEFAULT_PAGEINFO,
) => {
  return dig(data, `${path}.pageInfo`, fallback);
};

export const edgesFromData = (
  data: any,
  path: string,
  fallback: Array<any> = [],
) => {
  return dig(data, `${path}.edges`, fallback);
};

export const nodesFromData = (
  data: any,
  path: string,
  fallback: Array<any> = [],
) => {
  return edgesFromData(data, path, fallback).map((edge: any) => edge.node);
};

export const nodesFromDataWithoutEdges = (
  data: any,
  path: string,
  fallback: Array<any> = [],
) => {
  return dig(data, `${path}.nodes`, fallback);
};

export const mergeEdges = (data: any, edges: any, path: string) => {
  return immutable.set(
    data,
    `${path}.edges`,
    uniqBy(edges, (x: any) => x.node.id),
  );
};

export const removeEdgesFromDataById = (
  data: any,
  path: string,
  ids: string | Array<string>,
) => {
  const edges = edgesFromData(data, path);
  return mergeEdges(data, edgesWithoutNodeIds(edges, ids), path);
};

export const mergeNewPage = (
  oldData: any,
  { fetchMoreResult: newData }: { fetchMoreResult: any },
  path: string,
) => {
  if (!newData) {
    return oldData;
  }

  return mergeEdges(
    newData,
    [...edgesFromData(oldData, path), ...edgesFromData(newData, path)],
    path,
  );
};

export const setValueForPath = (data: any, path: string, value: any) => {
  return immutable.set(data, path, value);
};

export const photoUriFromVariants = (
  variants: Array<{ variant: string; uri: string }> = [],
  variant = 'THUMBNAIL',
) => {
  if (variants.length === 0) return null;

  return variants.find((img) => img.variant === variant).uri;
};

export const anyUnauthorizedErrors = (error: any) => {
  if (!error) {
    return false;
  }

  const graphQLErrors = dig(error, 'graphQLErrors', []);
  const anyUnauthorized = graphQLErrors.some((graphQLError: any) => {
    const code = dig(graphQLError, 'extensions.code');
    return code && code === 'unauthorized';
  });

  return anyUnauthorized;
};
